import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { cardSx, colors, typography } from '../constants';
import { useAnalytics } from '../hooks/useProfitData';
import { useDateRange } from '../hooks/useSelection';
import {
  DATE_OPTIONS,
  dateRangeLabel,
  fmt,
  fmtFriendly,
  negativeTone,
  pct,
} from '../lib/format';
import { useProfitIqAccess } from '../useProfitIqAccess';
import { Pill } from './Fields';
import { useUnsavedChangesGuard } from './UnsavedChanges';

/**
 * Profit IQ page header: the period summary strip plus the date-range filter
 * pills.
 *
 * The period total is the one figure present on every tab. A load is drawn by
 * the section loader, always in the same place, so this strip never adds a
 * second spinner. With a figure already there it keeps it, dimmed, while the
 * refresh settles.
 */
export default function ProfitIqHeader({ showDateBar = true }) {
  const { t } = useTranslation();
  const { range, setRange, customLabel, month, setMonth } = useDateRange();
  const guardMove = useUnsavedChangesGuard();
  const { data, isFetching } = useAnalytics();
  const { isTrial, trialEnd } = useProfitIqAccess();

  const [customOpen, setCustomOpen] = useState(false);

  const dateLabel = dateRangeLabel(range, month, customLabel);
  const score = data?.score ?? 0;
  const scoreColor = data?.scoreColor ?? '#9CA3AF';

  const handlePillClick = (id) => {
    if (id === 'custom') {
      setCustomOpen(true);
      return;
    }
    // A new period reloads the editors below, so unsaved work is asked about
    // before the figures are replaced.
    guardMove(() => {
      setRange(id);
      // Keep the costs-editing month in sync with the filter: "Last Month"
      // edits the previous calendar month, all other quick filters edit the
      // current one.
      const now = new Date();
      const d = new Date(
        now.getFullYear(),
        now.getMonth() - (id === 'prevmonth' ? 1 : 0),
        1
      );
      setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    });
  };

  return (
    <Box sx={{ ...cardSx, mb: 2, overflow: 'hidden' }}>
      {isTrial && trialEnd ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2.5,
            py: 1,
            backgroundColor: colors.accent,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 15, color: colors.primary }} />
          <Typography
            sx={{
              color: colors.primary,
              fontSize: typography.caption,
              fontWeight: 700,
            }}
          >
            {/* `fmtFriendly` reads a plain date, the config carries a timestamp. */}
            {t('profit_iq.trial_until', {
              date: fmtFriendly(trialEnd.slice(0, 10)),
            })}
          </Typography>
        </Box>
      ) : null}

      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          flexWrap: 'wrap',
          borderBottom: showDateBar ? `1px solid ${colors.border}` : 'none',
        }}
      >
        <Typography
          sx={{
            color: colors.mutedForeground,
            fontSize: typography.small,
            fontWeight: 600,
          }}
        >
          {dateLabel}
        </Typography>

        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              color: colors.foreground,
              fontSize: typography.emphasis,
              fontWeight: 700,
              opacity: isFetching ? 0.5 : 1,
            }}
          >
            {data ? fmt(data.totalRevenue) : '—'}
          </Typography>
          {data ? (
            data.hasData === false ? (
              <Typography
                sx={{
                  color: colors.faint,
                  fontSize: typography.caption,
                  fontWeight: 600,
                }}
              >
                No sales in this period
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: scoreColor,
                  fontSize: typography.caption,
                  fontWeight: 600,
                  ...negativeTone(data.profitPct),
                }}
              >
                Score: {score} · {pct(data.profitPct)} profit
              </Typography>
            )
          ) : null}
        </Box>
      </Box>

      {showDateBar ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 1.5,
            py: 1.25,
            overflowX: 'auto',
          }}
        >
          {DATE_OPTIONS.map((o) => (
            <Pill
              key={o.id}
              label={o.label}
              active={
                o.id === 'custom'
                  ? range === 'custom' || range === 'month'
                  : range === o.id
              }
              onClick={() => handlePillClick(o.id)}
            />
          ))}
        </Box>
      ) : null}

      <MonthPickerDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
      />
    </Box>
  );
}

/**
 * The "Custom" pill opens a month picker rather than an arbitrary range: the
 * mobile app works the same way, and the analytics endpoint only accepts a
 * calendar month here.
 */
function MonthPickerDialog({ open, onClose }) {
  const { setMonth, setRange } = useDateRange();
  const guardMove = useUnsavedChangesGuard();

  const months = Array.from({ length: 12 }, (_, off) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - off);
    const label =
      off === 0
        ? 'This month'
        : d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}`;
    return { off, label, value };
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogContent sx={{ p: 2.75 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: typography.subtitle,
              fontWeight: 700,
              color: colors.foreground,
            }}
          >
            Select a month
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon
              fontSize="small"
              sx={{ color: colors.mutedForeground }}
            />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: typography.caption,
            fontWeight: 700,
            color: colors.mutedForeground,
            letterSpacing: 0.5,
            mb: 1,
          }}
        >
          BY MONTH
        </Typography>

        <Grid container spacing={1}>
          {months.map((m) => (
            <Grid item xs={6} key={m.off}>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  guardMove(() => {
                    setMonth(m.value);
                    setRange('month');
                    onClose();
                  });
                }}
                sx={{
                  width: '100%',
                  p: 1.5,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  textAlign: 'left',
                  color: colors.primary,
                  backgroundColor: colors.accent,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2.5,
                  '&:hover': { filter: 'brightness(0.97)' },
                }}
              >
                {m.label}
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
